/**
 * Real-time event streaming via Server-Sent Events (SSE).
 *
 * Connects to the backend SSE endpoint and invalidates TanStack Query
 * caches when events arrive, causing affected components to refetch.
 *
 * Features:
 * - Exponential backoff reconnection (1s → 2s → 4s → ... → 30s cap)
 * - Token refresh on each reconnect (WorkOS tokens expire ~15min)
 * - Visibility API: disconnects when tab is hidden, reconnects when visible
 * - Connection status tracking for live indicator UI
 */

import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@workos-inc/authkit-react'
import { queryKeys } from '@/api/queries'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
const MAX_RECONNECT_DELAY = 30_000
const INITIAL_RECONNECT_DELAY = 1_000

type ConnectionStatus = 'connected' | 'connecting' | 'disconnected'

export function useRealtimeEvents(): { status: ConnectionStatus } {
  const queryClient = useQueryClient()
  const { getAccessToken } = useAuth()
  const [status, setStatus] = useState<ConnectionStatus>('disconnected')
  const eventSourceRef = useRef<EventSource | null>(null)
  const reconnectAttempts = useRef(0)
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isCleaningUp = useRef(false)

  useEffect(() => {
    isCleaningUp.current = false

    function disconnect() {
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current)
        reconnectTimer.current = null
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
      setStatus('disconnected')
    }

    async function connect() {
      // Don't connect if we're cleaning up
      if (isCleaningUp.current) return

      // Close existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }

      setStatus('connecting')

      let token: string
      try {
        token = await getAccessToken()
        if (!token) return
      } catch {
        // Auth failed
        setStatus('disconnected')
        return
      }

      // Don't connect if we cleaned up during token fetch
      if (isCleaningUp.current) return

      const url = `${API_BASE_URL}/api/v1/sse/stream?token=${encodeURIComponent(token)}`
      const es = new EventSource(url)
      eventSourceRef.current = es

      es.onopen = () => {
        console.log('[SSE] Connected to realtime stream')
        setStatus('connected')
        reconnectAttempts.current = 0
      }

      es.onerror = () => {
        console.warn('[SSE] Connection lost')
        setStatus('disconnected')
        es.close()
        eventSourceRef.current = null

        // Don't reconnect if we're cleaning up
        if (isCleaningUp.current) return

        // Exponential backoff
        const delay = Math.min(
          INITIAL_RECONNECT_DELAY * Math.pow(2, reconnectAttempts.current),
          MAX_RECONNECT_DELAY,
        )
        reconnectAttempts.current += 1
        reconnectTimer.current = setTimeout(() => {
          connect()
        }, delay)
      }

      // Event handlers that invalidate TanStack Query caches
      const handleEventCreated = (e: MessageEvent) => {
        console.log('[SSE] event:created', e.data)
        queryClient.invalidateQueries({ queryKey: ['events'] })
        queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats })
      }

      const handleEventUpdate = (e: MessageEvent) => {
        console.log(`[SSE] ${e.type}`, e.data)
        try {
          const data = JSON.parse(e.data) as { event_uuid?: string }
          if (data.event_uuid) {
            queryClient.invalidateQueries({ queryKey: queryKeys.event(data.event_uuid) })
          }
          queryClient.invalidateQueries({ queryKey: ['events'] })
          queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats })
        } catch {
          queryClient.invalidateQueries({ queryKey: ['events'] })
        }
      }

      const handleActionUpdate = (e: MessageEvent) => {
        console.log(`[SSE] ${e.type}`, e.data)
        try {
          const data = JSON.parse(e.data) as { event_uuid?: string }
          if (data.event_uuid) {
            queryClient.invalidateQueries({ queryKey: queryKeys.event(data.event_uuid) })
          }
        } catch {
          // Ignore parse errors
        }
      }

      es.addEventListener('event:created', handleEventCreated)
      es.addEventListener('event:analyzing', handleEventUpdate)
      es.addEventListener('event:completed', handleEventUpdate)
      es.addEventListener('event:error', handleEventUpdate)
      es.addEventListener('action:completed', handleActionUpdate)
      es.addEventListener('action:failed', handleActionUpdate)
    }

    connect()

    // Visibility API: disconnect when tab is hidden, reconnect when visible
    const handleVisibilityChange = () => {
      if (document.hidden) {
        disconnect()
      } else {
        reconnectAttempts.current = 0
        connect()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      isCleaningUp.current = true
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      disconnect()
    }
  }, [getAccessToken, queryClient])

  return { status }
}
